/**
 * The location create screen — `admin`/`manager` only, the same bar `LocationListPage`'s own
 * "Crear localidad" link is gated behind. `createLocationSchema` requires every field
 * `locationDetailShape` names except `id`/`createdAt`/`updatedAt`, including `latitude` and
 * `longitude` — both nullable, but the key itself is not optional, which is why this form asks
 * for two fields `LocationEditForm` does not: an update is a partial patch and can omit them, a
 * create cannot.
 *
 * This form talks to `LocationsApi` directly rather than through `useManagedLocationsList`,
 * mirroring `ContentFormPage`'s own reasoning: a create screen is not rendering the list it will
 * eventually append to, so there is nothing here for a list-shaped hook to manage.
 */
import type { CreateLocation, FieldError } from '@apuestapr/contracts';
import { Button, Field, Input, Textarea, buttonVariants, cn, toast } from '@apuestapr/ui';
import { useId, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import type { ApiFailure } from '../api/client.ts';
import { useSessionContext } from '../auth/SessionProvider.tsx';
import { createLocationsApi } from './api.ts';
import { locationPath, locationsRoutePath } from './paths.ts';

interface CreateFormState {
  readonly nombre: string;
  readonly ciudad: string;
  readonly direccion: string;
  readonly telefono: string;
  readonly latitude: string;
  readonly longitude: string;
  readonly estado: string;
  readonly notas: string;
  readonly ipAddress: string;
  readonly contactoNombre: string;
  readonly contactoTelefono: string;
  readonly licenciaJuego: string;
  readonly hasStaticIp: boolean;
  readonly isUpdated: boolean;
  readonly yubikeyAssigned: boolean;
  readonly ipWhitelisted: boolean;
  readonly training: boolean;
  readonly gcApproval: boolean;
  readonly otcUsersEnabled: boolean;
  readonly createLocationInKambi: boolean;
}

const emptyForm: CreateFormState = {
  nombre: '',
  ciudad: '',
  direccion: '',
  telefono: '',
  latitude: '',
  longitude: '',
  estado: 'activo',
  notas: '',
  ipAddress: '',
  contactoNombre: '',
  contactoTelefono: '',
  licenciaJuego: '',
  hasStaticIp: false,
  isUpdated: false,
  yubikeyAssigned: false,
  ipWhitelisted: false,
  training: false,
  gcApproval: false,
  otcUsersEnabled: false,
  createLocationInKambi: false,
};

/** Blank text fields become `null`; a blank coordinate becomes `null` rather than `NaN`. */
const payloadFrom = (state: CreateFormState): CreateLocation => ({
  nombre: state.nombre,
  ciudad: state.ciudad,
  direccion: state.direccion,
  telefono: state.telefono.trim() === '' ? null : state.telefono,
  latitude: state.latitude.trim() === '' ? null : Number(state.latitude),
  longitude: state.longitude.trim() === '' ? null : Number(state.longitude),
  estado: state.estado,
  notas: state.notas.trim() === '' ? null : state.notas,
  ipAddress: state.ipAddress.trim() === '' ? null : state.ipAddress,
  contactoNombre: state.contactoNombre.trim() === '' ? null : state.contactoNombre,
  contactoTelefono: state.contactoTelefono.trim() === '' ? null : state.contactoTelefono,
  licenciaJuego: state.licenciaJuego.trim() === '' ? null : state.licenciaJuego,
  hasStaticIp: state.hasStaticIp,
  isUpdated: state.isUpdated,
  yubikeyAssigned: state.yubikeyAssigned,
  ipWhitelisted: state.ipWhitelisted,
  training: state.training,
  gcApproval: state.gcApproval,
  otcUsersEnabled: state.otcUsersEnabled,
  createLocationInKambi: state.createLocationInKambi,
  tags: [],
});

const errorFor = (path: string, errors: readonly FieldError[]): string | undefined =>
  errors.find((error) => error.path === path)?.message;

const failureMessage = (failure: ApiFailure): string => {
  if (failure.kind === 'malformed') return 'La respuesta guardada no coincide con el contrato.';
  if (failure.kind === 'unreachable') return 'No se pudo conectar con la API.';
  if (failure.status === 403) return 'No tienes permiso para crear localidades.';
  return failure.message;
};

interface CheckboxRowProps {
  readonly label: string;
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly onChange: (checked: boolean) => void;
}

const CheckboxRow = ({ label, checked, disabled, onChange }: CheckboxRowProps): ReactElement => {
  const id = useId();
  return (
    <div className="flex items-center gap-2">
      <input
        id={id}
        type="checkbox"
        className="h-4 w-4 rounded border-input"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
      <label className="font-body text-sm text-foreground" htmlFor={id}>
        {label}
      </label>
    </div>
  );
};

export const LocationCreateForm = (): ReactElement => {
  const session = useSessionContext();
  const authenticated = session.state.status === 'authenticated' ? session.state.session : null;
  const api = useMemo(() => createLocationsApi(session.client), [session.client]);
  const navigate = useNavigate();

  const [state, setState] = useState<CreateFormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<readonly FieldError[]>([]);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  if (authenticated === null) return <></>;

  const set =
    <Key extends keyof CreateFormState>(key: Key) =>
    (value: CreateFormState[Key]): void =>
      setState((current) => ({ ...current, [key]: value }));

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setFailure(null);
    setErrors([]);
    setSaving(true);
    void api.create(payloadFrom(state), authenticated.csrfToken).then((result) => {
      setSaving(false);
      if (!result.ok) {
        session.handleFailure(result.failure);
        if (
          result.failure.kind === 'http' &&
          (result.failure.status === 400 || result.failure.status === 409)
        ) {
          setErrors(result.failure.details ?? []);
        } else {
          setFailure(result.failure);
        }
        return;
      }
      toast({ title: 'Localidad creada' });
      navigate(locationPath(result.data.id));
    });
  };

  return (
    <section aria-labelledby="location-create-heading" className="grid gap-6">
      <div>
        <h2 className="m-0" id="location-create-heading">
          Nueva localidad
        </h2>
        <p className="mb-0 text-muted-foreground">
          Los mismos campos operativos que la vista de edición, en un registro nuevo.
        </p>
      </div>

      {failure === null ? null : <p role="alert">{failureMessage(failure)}</p>}

      <form className="grid gap-6" noValidate onSubmit={submit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nombre" error={errorFor('nombre', errors)} required>
            {(control) => (
              <Input
                {...control}
                disabled={saving}
                value={state.nombre}
                onChange={(event) => set('nombre')(event.currentTarget.value)}
              />
            )}
          </Field>
          <Field label="Ciudad" error={errorFor('ciudad', errors)} required>
            {(control) => (
              <Input
                {...control}
                disabled={saving}
                value={state.ciudad}
                onChange={(event) => set('ciudad')(event.currentTarget.value)}
              />
            )}
          </Field>
          <Field
            className="sm:col-span-2"
            label="Dirección"
            error={errorFor('direccion', errors)}
            required
          >
            {(control) => (
              <Input
                {...control}
                disabled={saving}
                value={state.direccion}
                onChange={(event) => set('direccion')(event.currentTarget.value)}
              />
            )}
          </Field>
          <Field label="Teléfono" error={errorFor('telefono', errors)}>
            {(control) => (
              <Input
                {...control}
                disabled={saving}
                value={state.telefono}
                onChange={(event) => set('telefono')(event.currentTarget.value)}
              />
            )}
          </Field>
          <Field
            label="Estado"
            description="Texto libre: activo, no_activo, en_proceso, u otro valor operativo."
            error={errorFor('estado', errors)}
            required
          >
            {(control) => (
              <Input
                {...control}
                disabled={saving}
                value={state.estado}
                onChange={(event) => set('estado')(event.currentTarget.value)}
              />
            )}
          </Field>
          <Field label="Latitud" error={errorFor('latitude', errors)}>
            {(control) => (
              <Input
                {...control}
                disabled={saving}
                inputMode="decimal"
                type="number"
                value={state.latitude}
                onChange={(event) => set('latitude')(event.currentTarget.value)}
              />
            )}
          </Field>
          <Field label="Longitud" error={errorFor('longitude', errors)}>
            {(control) => (
              <Input
                {...control}
                disabled={saving}
                inputMode="decimal"
                type="number"
                value={state.longitude}
                onChange={(event) => set('longitude')(event.currentTarget.value)}
              />
            )}
          </Field>
        </div>

        <Field label="Notas" error={errorFor('notas', errors)}>
          {(control) => (
            <Textarea
              {...control}
              disabled={saving}
              rows={3}
              value={state.notas}
              onChange={(event) => set('notas')(event.currentTarget.value)}
            />
          )}
        </Field>

        <fieldset className="grid gap-4 rounded-xl border border-border p-4">
          <legend className="px-1 font-display text-sm font-medium">Contacto y red</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Dirección IP" error={errorFor('ipAddress', errors)}>
              {(control) => (
                <Input
                  {...control}
                  disabled={saving}
                  value={state.ipAddress}
                  onChange={(event) => set('ipAddress')(event.currentTarget.value)}
                />
              )}
            </Field>
            <Field label="Licencia de juego" error={errorFor('licenciaJuego', errors)}>
              {(control) => (
                <Input
                  {...control}
                  disabled={saving}
                  value={state.licenciaJuego}
                  onChange={(event) => set('licenciaJuego')(event.currentTarget.value)}
                />
              )}
            </Field>
            <Field label="Contacto: nombre" error={errorFor('contactoNombre', errors)}>
              {(control) => (
                <Input
                  {...control}
                  disabled={saving}
                  value={state.contactoNombre}
                  onChange={(event) => set('contactoNombre')(event.currentTarget.value)}
                />
              )}
            </Field>
            <Field label="Contacto: teléfono" error={errorFor('contactoTelefono', errors)}>
              {(control) => (
                <Input
                  {...control}
                  disabled={saving}
                  value={state.contactoTelefono}
                  onChange={(event) => set('contactoTelefono')(event.currentTarget.value)}
                />
              )}
            </Field>
          </div>
        </fieldset>

        <fieldset className="grid gap-3 rounded-xl border border-border p-4 sm:grid-cols-2">
          <legend className="px-1 font-display text-sm font-medium">Estado operativo</legend>
          <CheckboxRow
            label="IP estática"
            checked={state.hasStaticIp}
            disabled={saving}
            onChange={set('hasStaticIp')}
          />
          <CheckboxRow
            label="Actualizada"
            checked={state.isUpdated}
            disabled={saving}
            onChange={set('isUpdated')}
          />
          <CheckboxRow
            label="YubiKey asignada"
            checked={state.yubikeyAssigned}
            disabled={saving}
            onChange={set('yubikeyAssigned')}
          />
          <CheckboxRow
            label="IP en lista blanca"
            checked={state.ipWhitelisted}
            disabled={saving}
            onChange={set('ipWhitelisted')}
          />
          <CheckboxRow
            label="Capacitación completada"
            checked={state.training}
            disabled={saving}
            onChange={set('training')}
          />
          <CheckboxRow
            label="Aprobación GC"
            checked={state.gcApproval}
            disabled={saving}
            onChange={set('gcApproval')}
          />
          <CheckboxRow
            label="Usuarios OTC habilitados"
            checked={state.otcUsersEnabled}
            disabled={saving}
            onChange={set('otcUsersEnabled')}
          />
          <CheckboxRow
            label="Crear localidad en Kambi"
            checked={state.createLocationInKambi}
            disabled={saving}
            onChange={set('createLocationInKambi')}
          />
        </fieldset>

        <div className="flex flex-wrap gap-3">
          <Button loading={saving} loadingLabel="Creando localidad…" type="submit">
            Crear localidad
          </Button>
          <Link className={cn(buttonVariants({ variant: 'outline' }))} to={locationsRoutePath}>
            Volver a localidades
          </Link>
        </div>
      </form>
    </section>
  );
};
